import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Button, Flex } from 'styling/GlobalStyles'
import { toast } from 'react-toastify'
import {
  IAssignUsersToTeamResponse,
  IPreassignDataResponse,
} from 'features/operationalManagement/operationalManagement.types'
import {
  assignUsersToTeam,
  getBulkAssignPreview,
} from 'features/operationalManagement/operationalManagementSlice'
import { SelectEmployeesStep, SelectionMode } from './steps/SelectEmployeesStep'
import { TargetDatesStep } from './steps/TargetDatesStep'
import { PreFlightStep } from './steps/PreFlightStep'
import { ReasonConfirmStep } from './steps/ReasonConfirmStep'
import { ResultsStep } from './steps/ResultsStep'
import {
  BackChevron,
  BackTextButton,
  FooterLeft,
  FooterRight,
  FooterStep,
  StepCircle,
  StepConnector,
  StepItem,
  StepLabel,
  Stepper,
  WizardFooter,
  WizardShell,
  WizardSubtitle,
  WizardTitle,
  WizardTopRow,
} from './BulkAssign.styled'

interface Props {
  onClose: () => void
}

const STEP_LABELS = [
  'Select Employees',
  'Target & Dates',
  'Pre-flight',
  'Reason & Confirm',
  'Results',
]

const makeBatchId = () => `BA-${String(Math.floor(Math.random() * 9999)).padStart(4, '0')}`

export const BulkAssignWizard = ({ onClose }: Props) => {
  const dispatch = useDispatch<AppDispatch>()
  const teamDetailsById = useSelector(
    (state: RootState) => state.operationalManagement.teamDetailsById,
  )
  const assigning = useSelector(
    (state: RootState) => state.operationalManagement.assigningEmployees,
  )

  const [step, setStep] = useState(1)
  const [batchId] = useState(makeBatchId)
  const [mode, setMode] = useState<SelectionMode>('manual')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [targetTeamId, setTargetTeamId] = useState<number | null>(null)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [reason, setReason] = useState('')

  const [loadingPreview, setLoadingPreview] = useState(false)
  const [preassignData, setPreassignData] = useState<IPreassignDataResponse | null>(null)
  const [result, setResult] = useState<IAssignUsersToTeamResponse | null>(null)
  const [executedAt, setExecutedAt] = useState<Date>(new Date())
  const [showStep2Errors, setShowStep2Errors] = useState(false)
  const [showStep4Errors, setShowStep4Errors] = useState(false)

  const targetTeamName = useMemo(() => {
    if (targetTeamId == null) return ''
    return teamDetailsById[targetTeamId]?.name ?? ''
  }, [targetTeamId, teamDetailsById])

  const startOfToday = () => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }
  const isBeforeToday = (d: Date) => {
    const today = startOfToday()
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() < today.getTime()
  }

  const canProceed = useMemo(() => {
    switch (step) {
      case 1:
        return selected.size > 0
      case 2:
        return !loadingPreview
      case 3:
        return !!preassignData
      case 4:
        return !assigning
      default:
        return true
    }
  }, [step, selected.size, preassignData, assigning, loadingPreview])

  const goBack = () => {
    if (step === 1) {
      onClose()
      return
    }
    setStep((s) => Math.max(1, s - 1))
  }

  const goNext = async () => {
    if (step === 2) {
      const errors: string[] = []
      if (targetTeamId == null) errors.push('Target team is required')
      if (!startDate) errors.push('Effective Start is required')
      if (startDate && isBeforeToday(startDate))
        errors.push('Effective Start cannot be in the past')
      if (endDate && isBeforeToday(endDate)) errors.push('Effective End cannot be in the past')
      if (endDate && startDate && endDate.getTime() < startDate.getTime())
        errors.push('Effective End must be after Effective Start')
      if (errors.length) {
        setShowStep2Errors(true)
        toast.warn(errors[0])
        return
      }
      if (!targetTeamId || !startDate) return
      setShowStep2Errors(false)
      setLoadingPreview(true)
      try {
        const res = await dispatch(
          getBulkAssignPreview({
            userIds: Array.from(selected),
            operationalTeamId: targetTeamId,
            startDate: startDate.toISOString(),
            ...(endDate ? { endDate: endDate.toISOString() } : {}),
          }),
        )
        if (getBulkAssignPreview.fulfilled.match(res)) {
          setPreassignData(res.payload as IPreassignDataResponse)
          setStep(3)
        }
      } finally {
        setLoadingPreview(false)
      }
      return
    }

    if (step === 4) {
      if (!reason.trim()) {
        setShowStep4Errors(true)
        toast.warn('Please provide a reason for this assignment')
        return
      }
      setShowStep4Errors(false)
      if (!targetTeamId || !startDate) return
      const res = await dispatch(
        assignUsersToTeam({
          userIds: Array.from(selected),
          operationalTeamId: targetTeamId,
          startDate: startDate.toISOString(),
          ...(endDate ? { endDate: endDate.toISOString() } : {}),
          reason: reason.trim(),
        }),
      )
      if (assignUsersToTeam.fulfilled.match(res)) {
        setResult(res.payload.response)
        setExecutedAt(new Date())
        setStep(5)
      } else {
        toast.error('Bulk assignment failed')
      }
      return
    }

    setStep((s) => Math.min(5, s + 1))
  }

  const resetWizard = () => {
    setStep(1)
    setSelected(new Set())
    setTargetTeamId(null)
    setStartDate(null)
    setEndDate(null)
    setReason('')
    setPreassignData(null)
    setResult(null)
  }

  const stepState = (i: number): 'done' | 'active' | 'pending' =>
    i < step ? 'done' : i === step ? 'active' : 'pending'

  return (
    <WizardShell>
      <WizardTopRow>
        <BackChevron type='button' onClick={onClose} title='Back'>
          ←
        </BackChevron>
        <WizardTitle>Bulk Assignment Tool</WizardTitle>
      </WizardTopRow>
      <WizardSubtitle>
        Assign multiple employees to a team in a single auditable batch
      </WizardSubtitle>

      <Stepper>
        {STEP_LABELS.map((label, i) => {
          const idx = i + 1
          const state = stepState(idx)
          return (
            <React.Fragment key={label}>
              <StepItem>
                <StepCircle state={state}>{state === 'done' ? '✓' : idx}</StepCircle>
                <StepLabel state={state}>{label}</StepLabel>
              </StepItem>
              {i < STEP_LABELS.length - 1 && <StepConnector />}
            </React.Fragment>
          )
        })}
      </Stepper>

      {step === 1 && (
        <SelectEmployeesStep
          mode={mode}
          onModeChange={setMode}
          selected={selected}
          onChangeSelected={setSelected}
        />
      )}
      {step === 2 && (
        <TargetDatesStep
          selectedCount={selected.size}
          targetTeamId={targetTeamId}
          onTargetTeamChange={setTargetTeamId}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          showErrors={showStep2Errors}
        />
      )}
      {step === 3 && <PreFlightStep loading={loadingPreview} data={preassignData} />}
      {step === 4 && (
        <ReasonConfirmStep
          batchId={batchId}
          employeeCount={selected.size}
          targetTeamName={targetTeamName}
          reason={reason}
          onReasonChange={setReason}
          showErrors={showStep4Errors}
        />
      )}
      {step === 5 && (
        <ResultsStep
          batchId={batchId}
          targetTeamName={targetTeamName}
          executedAt={executedAt}
          result={result}
          onNewBatch={resetWizard}
        />
      )}

      {step !== 5 && (
        <WizardFooter>
          <FooterLeft>
            <BackTextButton type='button' onClick={goBack} disabled={loadingPreview || assigning}>
              ← {step === 1 ? 'Cancel' : 'Back'}
            </BackTextButton>
          </FooterLeft>
          <FooterStep>
            Step {step} of {STEP_LABELS.length}
          </FooterStep>
          <FooterRight>
            <Button
              orange
              width='160'
              onClick={goNext}
              disabled={!canProceed || loadingPreview || assigning}
            >
              {step === 4
                ? assigning
                  ? 'Executing…'
                  : 'Execute batch'
                : step === 2 && loadingPreview
                ? 'Loading…'
                : 'Next →'}
            </Button>
          </FooterRight>
        </WizardFooter>
      )}

      {step === 5 && (
        <Flex justcenter mt='16'>
          <Button white onClick={onClose}>
            Close
          </Button>
        </Flex>
      )}
    </WizardShell>
  )
}
